import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import authService from '../services/auth.service';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is already logged in
    const storedUser = authService.getUser();
    const storedToken = authService.getToken();
    
    console.log('[AuthContext] Session restoration:', {
      hasUser: !!storedUser,
      hasToken: !!storedToken,
      user: storedUser
    });
    
    if (storedUser && storedToken) {
      setUser(storedUser);
      console.log('[AuthContext] Session restored successfully');
    } else {
      console.log('[AuthContext] No valid session found');
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const response = await authService.login(email, password);
      // Ensure user is set correctly from response
      const userData = response?.user || response?.data?.user || response?.data || response;
      setUser(userData);
      console.log('[AuthContext] Login successful:', { user: userData, hasToken: !!response?.token || !!response?.data?.token });
      return response;
    } catch (err) {
      console.error('[AuthContext] Login failed:', err);
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, fullName, password) => {
    try {
      setError(null);
      setLoading(true);
      const response = await authService.register(email, fullName, password);
      // Ensure user is set correctly from response
      const userData = response?.user || response?.data?.user || response?.data || response;
      setUser(userData);
      return response;
    } catch (err) {
      setError(err.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await authService.logout();
      setUser(null);
    } catch (err) {
      setError(err.message || 'Logout failed');
      throw err;
    }
  };

  const value = useMemo(() => {
    const role = user?.role || user?.type || user?.userType;
    const isAdmin = !!user && role === 'admin';

    return {
      user,
      loading,
      error,
      login,
      register,
      logout,
      isLoggedIn: !!user,
      isAdmin
    };
  }, [user, loading, error]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
